import { AccessSchedule } from 'utils/constants/access-schedule';
import capitalize from '../capitalize';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export class GuestHelper {
    static formatPerson(obj) {
        const isOTC = obj?.starts_at && obj?.expires_at;
        if (isOTC) {
            return capitalize(obj?.name);
        }

        const first = capitalize(obj?.first_name?.trim());
        const last = capitalize(obj?.last_name?.trim());
        const company = capitalize(obj?.company_name?.trim());

        const name = [first, last].filter(Boolean).join(' ');

        if (name && company) return `${name} - ${company}`;
        if (name) return name;
        if (company) return company;

        return 'Unknown';
    }

    static formatName(obj) {
        const isOTC = obj?.starts_at && obj?.expires_at;
        if (isOTC) {
            return capitalize(obj?.name);
        }

        const first = capitalize(obj?.first_name?.trim());
        const last = capitalize(obj?.last_name?.trim());
        const company = capitalize(obj?.company_name?.trim());

        const name = [last, first].filter(Boolean).join(' ');

        if (name && company) return `${name} - ${company}`;
        if (name) return name;
        if (company) return company;

        return 'Unknown';
    }

    static formatFirestoreTimestamp(timestamp) {
        if (!timestamp) return '';

        let date;
        if (typeof timestamp._seconds === 'number') {
            date = new Date(timestamp._seconds * 1000 + (timestamp._nanoseconds || 0) / 1e6);
        } else if (typeof timestamp.seconds === 'number') {
            date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1e6);
        } else if (typeof timestamp.__time__ === 'string') {
            date = new Date(timestamp.__time__);
        } else {
            return '';
        }

        if (Number.isNaN(date.getTime())) return '';

        return date.toLocaleString('en-US', {
            month: 'short', // Feb
            day: 'numeric', // 8
            year: 'numeric', // 2026
            hour: 'numeric', // 9
            minute: '2-digit',
            hour12: true // AM/PM
        });
    }

    // Normalizes the various timestamp shapes the API can return
    // ({ _seconds, _nanoseconds }, { seconds, nanoseconds }, { __time__: isoString }, ISO string, Date)
    // into epoch milliseconds so timestamps can be reliably compared.
    static getTimestampMillis(value) {
        if (!value) return null;
        if (typeof value === 'string') return new Date(value).getTime();
        if (value instanceof Date) return value.getTime();
        if (typeof value.__time__ === 'string') return new Date(value.__time__).getTime();
        if (typeof value._seconds === 'number') {
            return value._seconds * 1000 + (value._nanoseconds || 0) / 1e6;
        }
        if (typeof value.seconds === 'number') {
            return value.seconds * 1000 + (value.nanoseconds || 0) / 1e6;
        }
        return null;
    }

    static formatPhoneNumber(phone) {
        if (!phone?.number) return '';

        const fullNumber = phone.code
            ? `+${phone.code}${phone.number}`
            : phone.number.toString();

        const parsed = parsePhoneNumberFromString(fullNumber);

        if (!parsed) return phone.number.toString();

        if (parsed.country === 'US' || parsed.country === 'CA') {
            const national = parsed.nationalNumber;

            return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(
                6
            )}`;
        }

        return parsed.formatInternational();
    }

    static getAccessDescription(guest) {
        if (!guest) return '';

        const isOTC = guest?.starts_at && guest?.expires_at;

        if (isOTC) {
            const startsAtMillis = this.getTimestampMillis(guest.starts_at);
            const createdAtMillis = this.getTimestampMillis(guest.created_at);
            const showStartsAt =
                startsAtMillis !== null &&
                createdAtMillis !== null &&
                startsAtMillis !== createdAtMillis &&
                startsAtMillis > Date.now();

            let description = 'One-time access';

            if (showStartsAt) {
                description += `, starts at: ${this.formatFirestoreTimestamp(
                    guest.starts_at
                )}`;
            }

            description += `, expires on: ${this.formatFirestoreTimestamp(
                guest.expires_at
            )}`;

            return description;
        }

        const hasRecurringAccess = guest?.access_days?.length > 0;
        const hasStartDate = !!guest?.access_begins;
        const hasEndDate = !!guest?.access_expires;

        const hasStartTime = !!guest?.access_start_time;
        const hasEndTime = !!guest?.access_end_time;

        if (hasRecurringAccess) {
            if (!hasStartTime && !hasEndTime) {
                return 'Anytime access on selected days';
            }

            if (hasStartTime && hasEndTime) {
                const start = formatAccessTime(guest.access_start_time);
                const end = formatAccessTime(guest.access_end_time);

                return `Limited access on selected days from ${start} to ${end}`;
            }

            return 'Limited access on selected days';
        }

        const isLimitedAccess = hasStartTime || hasEndTime || hasEndDate;

        let description = isLimitedAccess ? 'Limited access' : 'Anytime access';

        if (hasStartDate) {
            description += ` from ${this.formatFirestoreTimestamp(guest.access_begins)}`;
        }

        if (hasEndDate) {
            description += ` until ${this.formatFirestoreTimestamp(
                guest.access_expires
            )}`;
        }

        return description;
    }

    static formatLabel(string) {
        return string
            .replace(/[_-]+/g, ' ')
            .trim()
            .replace(/\s+/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase());
    }

    static categorizeGuestAccess(guests, nowSeconds = Math.floor(Date.now() / 1000)) {
        const sortedGuests = this.sortByCreationDate(guests);
        return sortedGuests.reduce(
            (acc, guest) => {
                if (!guest) return acc;

                const isOTC = guest?.starts_at && guest?.expires_at;
                const begins = isOTC
                    ? guest.starts_at._seconds
                    : guest.access_begins?._seconds ?? null;
                const expires = isOTC
                    ? guest.expires_at._seconds
                    : guest.access_expires?._seconds ?? null;

                if (
                    guest?.status === 'invited' ||
                    guest?.status === 'pending_approval'
                ) {
                    acc.pending.push({ ...guest, chipLabel: 'Pending' });
                    return acc;
                }
                if (guest?.suspended) {
                    acc.expired.push({ ...guest, chipLabel: 'Suspended' });
                    return acc;
                }

                const hasRecurringAccess = !isOTC && guest?.access_days?.length > 0;

                if (hasRecurringAccess) {
                    const chipLabel =
                        guest.object_type === 'visitor'
                            ? 'Visitor'
                            : guest.type === 'vendor'
                            ? 'Vendor'
                            : 'Guest';

                    acc.active.push({ ...guest, chipLabel });
                    return acc;
                }
                if (begins !== null && begins > nowSeconds) {
                    acc.scheduled.push({ ...guest, chipLabel: 'Scheduled' });
                    return acc;
                }
                if (expires !== null && expires < nowSeconds) {
                    acc.expired.push({ ...guest, chipLabel: 'Expired' });
                    return acc;
                }
                if (begins !== null && begins <= nowSeconds) {
                    const chipLabel =
                        guest.object_type === 'visitor' || isOTC
                            ? 'Visitor'
                            : guest.type === 'vendor'
                            ? 'Vendor'
                            : 'Guest';
                    acc.active.push({ ...guest, chipLabel });
                    return acc;
                }

                acc.expired.push({ ...guest, chipLabel: 'Expired' });
                return acc;
            },
            { active: [], scheduled: [], pending: [], expired: [] }
        );
    }

    static getGuestCategory(guest, nowSeconds = Math.floor(Date.now() / 1000)) {
        if (!guest) return { category: null, chipLabel: null };

        const isOTC = guest?.starts_at && guest?.expires_at;
        const begins = isOTC
            ? guest.starts_at._seconds
            : guest.access_begins?._seconds ?? null;
        const expires = isOTC
            ? guest.expires_at._seconds
            : guest.access_expires?._seconds ?? null;

        if (
            guest?.status === 'invited' ||
            guest?.status === 'pending_approval'
        ) {
            return { category: 'pending', chipLabel: 'Pending' };
        }
        if (guest?.suspended) {
            return { category: 'expired', chipLabel: 'Suspended' };
        }
        if (begins !== null && begins > nowSeconds) {
            return { category: 'scheduled', chipLabel: 'Scheduled' };
        }
        if (expires !== null && expires < nowSeconds) {
            return { category: 'expired', chipLabel: 'Expired' };
        }
        if (begins !== null && begins <= nowSeconds) {
            const chipLabel =
                guest.object_type === 'visitor' || isOTC
                    ? 'Visitor'
                    : guest.type === 'vendor'
                    ? 'Vendor'
                    : 'Guest';
            return { category: 'active', chipLabel };
        }
        return { category: 'expired', chipLabel: 'Expired' };
    }

    static getGuestAccessType(guest) {
        if (!guest) return null;

        const isRecurring =
            Array.isArray(guest?.access_days) ||
            guest?.access_start_time !== null ||
            guest?.access_end_time !== null;

        if (isRecurring) return AccessSchedule.RECURRING;
        if (guest?.access_expires) return AccessSchedule.TEMPORARY;
        return AccessSchedule.ALWAYS;
    }
    static sortByCreationDate(array, direction = 'desc') {
        return [...array].sort((a, b) => {
            const getSeconds = obj => {
                if (!obj) return 0;

                if (obj._seconds) return obj._seconds;

                if (obj instanceof Date) return obj.getTime() / 1000;

                if (typeof obj === 'string') return new Date(obj).getTime() / 1000;

                return 0;
            };

            const dateA = getSeconds(a.created_at || a.access_created);
            const dateB = getSeconds(b.created_at || b.access_created);

            return direction === 'asc' ? dateA - dateB : dateB - dateA;
        });
    }

    static withGuestCategoryLabels(guests) {
    const categorized = this.categorizeGuestAccess(guests);

    return this.sortByCreationDate([
        ...categorized.active,
        ...categorized.scheduled,
        ...categorized.pending,
        ...categorized.expired
    ]);
}
}

export const formatAccessTime = timeObj => {
    if (!timeObj || timeObj.hours === undefined) return '';

    const date = new Date();
    date.setHours(timeObj.hours, timeObj.minutes || 0, 0);

    return date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit'
    });
};
